import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HttpService from "../../Hooks/Https-services";
import { APIRoutes, PageRoutes } from "../../Utils/constant";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { Tv, PlayCircle } from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import VideoPlayerDialog from "./VideoPlayerDialog";
import HighlightPlayer from "../../Components/VideoPlayer";
import { useLoadData } from "../../Context/LoadDataContext";
import { toast } from "react-toastify";

interface GameEvent {
  id: number;
  period: number;
  event_type: string;
  start: number;
  end: number;
  x: string;
  y: string;
  home_players_number: number;
  away_players_number: number;
  home_score: number;
  away_score: number;
  video_url: string;
  real_assist_type: string;
  empty_net: string;
  player: {
    player_id: number;
    player_first_name: string;
    player_last_name: string;
    player_jersey: number;
  };
  home_team: {
    team_id: number;
    team_name: string;
  };
  away_team: {
    team_id: number;
    team_name: string;
  };
  game: {
    game_id: number;
    date: string;
    home_score: number;
    away_score: number;
  };
}

interface PaginationData {
  total: number;
  totalPages: number;
  currentPage: number;
  limit: number;
}

interface EditFormValues {
  id: number;
  player_id: number;
  period: number;
  start: number;
  end: number;
  player: any;
  event_type: string;
  x: string;
  y: string;
  home_players_number: number;
  away_players_number: number;
  home_score: number;
  away_score: number;
}

const validationSchema = Yup.object({
  period: Yup.number().required("Period is required").min(1).max(5),
  start: Yup.number().required("Start time is required"),
  end: Yup.number().required("End time is required"),
  event_type: Yup.string().required("Event type is required"),
  x: Yup.string(),
  player: Yup.object().required("Player is required"),
  y: Yup.string(),
  home_players_number: Yup.number().required("Home players number is required"),
  away_players_number: Yup.number().required("Away players number is required"),
  home_score: Yup.number().required("Home score is required"),
  away_score: Yup.number().required("Away score is required"),
});

const EditFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  data: GameEvent | null;
  onRefresh: () => void;
  gameDetails: any | null;
  sortedPlayers: any[];
  teamMap: Record<string, string>;
}> = ({
  open,
  teamMap,
  onClose,
  data,

  onRefresh,
  gameDetails,
  sortedPlayers,
}) => {
  const { id } = useParams();
  const [events] = useState<string[]>([
    "SAVE_LEFT_PAD",
    "SAVE_GLOVE",
    "SAVE_BODY",
    "PASS",
    "SKATING_WITH_THE_PUCK",
    "SAVE_RIGHT_PAD",
    "SAVE_BLOCKER",
    "SAVE_STICK",
    "RECEPTION",
    "SAVE_HEAD",
    "SHIFT",
    "CHECK_STICK",
    "SHOT_ATTEMPT_BLOCKED",
    "HIT_DRAWN",
    "SHOT_ATTEMPT_LOWER_LEFT",
    "SHOT_ATTEMPT_LOWER_RIGHT",
    "HIT_TAKEN",
    "GOAL",
    "SHOT_ATTEMPT_UPPER_LEFT",
    "FACE_OFF",
    "SHOT_ATTEMPT_BOTTOM_MIDDLE",
    "SHOT_ATTEMPT_WIDE_LEFT_LOW",
    "PENALTY_2_MIN",
    "SHOT_ATTEMPT_DEFLECTED",
    "SHOT_ATTEMPT_OVER_THE_NET_RIGHT",
    "SHOT_ATTEMPT_WIDE_RIGHT_HIGH",
    "SHOT_ATTEMPT_TOP_MIDDLE",
    "SHOT_ATTEMPT_WIDE_LEFT_HIGH",
    "SHOT_ATTEMPT_WIDE_RIGHT_LOW",
    "SHOT_ATTEMPT_UPPER_RIGHT",
    "SHOT_ATTEMPT_OVER_THE_NET_LEFT",
    "PENALTY_5_MIN",
    "PENALTY_4_MIN",
    "WHISTLE",
    "SHOT_ATTEMPT_WIDE_LEFT",
    "SHOT_ATTEMPT_WIDE_RIGHT",
    "OFF_THE_BOARDS",
    "SAVE_HAND",
  ]);
  const [jsonValue, setJsonValue] = useState("");
  const editorRef = useRef<any>(null);

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  const formik = useFormik<EditFormValues>({
    initialValues: {
      id: data?.id || 0,
      player_id: data?.player?.player_id || 0,
      player: data?.player || null,
      period: data?.period || 1,
      start: data?.start || 0,
      end: data?.end || 0,

      event_type: data?.event_type || "",
      x: data?.x || "",
      y: data?.y || "",
      home_players_number: data?.home_players_number || 5,
      away_players_number: data?.away_players_number || 5,
      home_score: data?.home_score || 0,
      away_score: data?.away_score || 0,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // TODO: Update game event
      let payload: any = {
        player_id: values.player?.player_id || null,
        period: values.period || null,
        start: values.start || null,
        end: values.end || null,
        event_type: values.event_type || null,
        x: values.x || null,
        y: values.y || null,
        home_players_number: values.home_players_number || null,
        away_players_number: values.away_players_number || null,
        home_score: values.home_score || null,
        away_score: values.away_score || null,
      };
      try {
        if (values.id !== 0) {
          await HttpService.patch(
            APIRoutes.UpdateGameEvent.replace(":id", values.id.toString()),
            payload
          );
        } else {
          payload.game_id = id;
          await HttpService.post(APIRoutes.AddGameEvent, payload);
        }
        toast.success("Game event saved successfully");
        onRefresh();
        resetForm();
        if (values.id !== 0) onClose();
      } catch (error: any) {
        console.error(error);
        toast.error(
          error?.response?.data?.message || "Failed to save game event"
        );
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });
  const { playerRef } = useLoadData();
  useEffect(() => {
    if (data) {
      playerRef.current?.seek(data.start);
    }
  }, [data, playerRef]);
  const handleEditorChange = (value: string | undefined) => {
    if (!value) return;
    setJsonValue(value);
    try {
      const parsedData = JSON.parse(value);
      formik.setValues(
        {
          ...formik.values,
          period: parsedData.period ?? formik.values.period,
          player_id: parsedData.player_id ?? formik.values.player_id,
          start: parsedData.start ?? formik.values.start,
          end: parsedData.end ?? formik.values.end,
          event_type: parsedData.event_type ?? formik.values.event_type,
          x: parsedData.x ?? formik.values.x,
          y: parsedData.y ?? formik.values.y,
          home_players_number:
            parsedData.home_players_number ?? formik.values.home_players_number,
          away_players_number:
            parsedData.away_players_number ?? formik.values.away_players_number,
          home_score: parsedData.home_score ?? formik.values.home_score,
          away_score: parsedData.away_score ?? formik.values.away_score,
        },
        true
      );
    } catch (error) {
      console.error("Error parsing JSON:", error);
    }
  };

  const handleCapture = () => {
    console.log("Capture");
    const currentTime = playerRef.current.getCurrentTime("relativetime");
    formik.setFieldValue("start", currentTime.toFixed(1));
    formik.setFieldValue("end", (currentTime + 10).toFixed(1));
  };
  useEffect(() => {
    const jsonData = {
      period: formik.values.period,
      player_id: formik.values.player_id,
      start: formik.values.start,
      end: formik.values.end,
      event_type: formik.values.event_type,
      x: formik.values.x,
      y: formik.values.y,
      home_players_number: formik.values.home_players_number,
      away_players_number: formik.values.away_players_number,
      home_score: formik.values.home_score,
      away_score: formik.values.away_score,
    };
    setJsonValue(JSON.stringify(jsonData, null, 2));
  }, [formik.values]);
  const handlePaste = (e: React.ClipboardEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      if (formik.values.id === 0) {
        const pastedData = e.clipboardData.getData("Text");
        const parsedData = JSON.parse(pastedData);

        formik.setValues(
          {
            id: 0,
            player_id: parsedData.player_id || 0,
            period: parsedData.period || 1,
            start: parsedData.start || 0,
            end: parsedData.end || 0,
            event_type: parsedData.event_type || "",
            x: parsedData.x || "",
            y: parsedData.y || "",
            home_players_number: parsedData.home_players_number || 5,
            away_players_number: parsedData.away_players_number || 5,
            home_score: parsedData.home_score || 0,
            away_score: parsedData.away_score || 0,
            player: parsedData.player || null,
          },
          true
        );
      }
    } catch (error) {
      console.error("Error parsing pasted data:", error);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      onPaste={handlePaste}
    >
      <DialogTitle>Edit Game Event</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <HighlightPlayer url={gameDetails?.watch_url} />
          <Box sx={{ py: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleCapture}
            >
              Capture
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="period"
                label="Period"
                type="number"
                value={formik.values.period}
                onChange={formik.handleChange}
                error={formik.touched.period && Boolean(formik.errors.period)}
                helperText={formik.touched.period && formik.errors.period}
              />
            </Grid>
            <Grid item xs={3}>
              <Autocomplete
                id="players-select"
                fullWidth
                options={sortedPlayers}
                disableCloseOnSelect
                getOptionLabel={(option) =>
                  `${option.player_first_name} ${option.player_last_name}`
                }
                groupBy={(option) => teamMap[option.team_id] || "Unknown Team"}
                value={formik.values.player}
                onChange={(_, newValue) =>
                  formik.setFieldValue("player", newValue)
                }
                clearIcon={null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={
                      formik.touched.player && Boolean(formik.errors.player)
                    }
                    helperText={formik.touched.player ? "" : ""}
                    label="Player"
                    placeholder={"Player"}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.player_id}>
                    {`${option.player_first_name} ${option.player_last_name}`}
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="start"
                label="Start Time"
                type="number"
                value={formik.values.start}
                onChange={formik.handleChange}
                error={formik.touched.start && Boolean(formik.errors.start)}
                helperText={formik.touched.start && formik.errors.start}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="end"
                label="End Time"
                type="number"
                value={formik.values.end}
                onChange={formik.handleChange}
                error={formik.touched.end && Boolean(formik.errors.end)}
                helperText={formik.touched.end && formik.errors.end}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                name="event_type"
                label="Event Type"
                value={formik.values.event_type}
                onChange={formik.handleChange}
                error={
                  formik.touched.event_type && Boolean(formik.errors.event_type)
                }
                helperText={
                  formik.touched.event_type && formik.errors.event_type
                }
              >
                {events.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="x"
                label="X Coordinate"
                value={formik.values.x}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="y"
                label="Y Coordinate"
                value={formik.values.y}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="home_players_number"
                label="Home Players"
                type="number"
                value={formik.values.home_players_number}
                onChange={formik.handleChange}
                error={
                  formik.touched.home_players_number &&
                  Boolean(formik.errors.home_players_number)
                }
                helperText={
                  formik.touched.home_players_number &&
                  formik.errors.home_players_number
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="away_players_number"
                label="Away Players"
                type="number"
                value={formik.values.away_players_number}
                onChange={formik.handleChange}
                error={
                  formik.touched.away_players_number &&
                  Boolean(formik.errors.away_players_number)
                }
                helperText={
                  formik.touched.away_players_number &&
                  formik.errors.away_players_number
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="home_score"
                label="Home Score"
                type="number"
                value={formik.values.home_score}
                onChange={formik.handleChange}
                error={
                  formik.touched.home_score && Boolean(formik.errors.home_score)
                }
                helperText={
                  formik.touched.home_score && formik.errors.home_score
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                name="away_score"
                label="Away Score"
                type="number"
                value={formik.values.away_score}
                onChange={formik.handleChange}
                error={
                  formik.touched.away_score && Boolean(formik.errors.away_score)
                }
                helperText={
                  formik.touched.away_score && formik.errors.away_score
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
          >
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const GameViewDetail: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<GameEvent | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [events, setEvents] = useState<GameEvent[]>([]);
  const [gameDetails, setGameDetails] = useState<any | null>(null);
  const [game, setGame] = useState<any | null>(null);
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  // Handle URL params for modals
  useEffect(() => {
    const eventId = searchParams.get("eventId");
    const modalType = searchParams.get("modal");

    if (eventId) {
      const event = events.find((e) => e.id.toString() === eventId);
      if (event) {
        setSelectedEvent(event);
        if (modalType === "edit") {
          setIsEditModalOpen(true);
        } else if (modalType === "video") {
          setIsVideoModalOpen(true);
        }
      }
    }
  }, [events, searchParams]);
  const [players, setPlayers] = useState<any>([]);
  const teamMap = useMemo(() => {
    return [
      {
        team_id: game?.home_team_id,
        team_name: gameDetails?.home_team,
      },
      {
        team_id: game?.away_team_id,
        team_name: gameDetails?.away_team,
      },
    ].reduce((acc, team) => {
      acc[team.team_id] = team.team_name;
      return acc;
    }, {} as Record<string, string>);
  }, [game, gameDetails]);
  const sortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => {
      const teamA = teamMap[a.team_id] || "Unknown Team";
      const teamB = teamMap[b.team_id] || "Unknown Team";
      if (teamA !== teamB) {
        return teamA.localeCompare(teamB);
      }
      return `${a.player_first_name} ${a.player_last_name}`.localeCompare(
        `${b.player_first_name} ${b.player_last_name}`
      );
    });
  }, [players, teamMap]);

  const GetAllPlayers = useCallback(async () => {
    if (!game?.home_team_id || !game?.away_team_id) return;

    try {
      const query = {
        team_ids: [game?.home_team_id, game?.away_team_id].join(","),
        limit: "10000",
      };
      const res = await HttpService.get(
        APIRoutes.GetAllPlayers + `?${new URLSearchParams(query).toString()}`
      );
      setPlayers(res.data.data);
    } catch (error) {
      console.log(error);
    }
  }, [game]);
  useEffect(() => {
    GetAllPlayers();
  }, [GetAllPlayers]);
  const GetGameDetails = useCallback(async () => {
    try {
      const res = await HttpService.getAdmin(
        APIRoutes.GetGameDetails.replace(":id", id!)
      );
      setGameDetails(res.data.data[0]);
    } catch (error) {
      console.error(error);
    }
  }, [id]);
  useEffect(() => {
    GetGameDetails();
  }, [GetGameDetails]);

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 90, sortable: false },
    { field: "period", headerName: "Period", width: 70, sortable: false },
    {
      field: "event_type",
      headerName: "Event Type",
      width: 250,
      sortable: false,
    },
    {
      field: "time",
      headerName: "Time",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        try {
          return params.row.start
            ? `${moment(params.row.start * 1000).format("mm:ss")}`
            : "--:--";
        } catch (error) {
          return "--:--";
        }
      },
    },
    {
      field: "player",
      headerName: "Player",
      width: 180,
      sortable: false,
      renderCell: (params) => {
        try {
          const player = params.row.player;
          if (!player) return "N/A";
          const name = [
            player.player_first_name || "",
            player.player_last_name || "",
          ]
            .filter(Boolean)
            .join(" ");
          const jersey = player.player_jersey
            ? ` (#${player.player_jersey})`
            : "";
          return name ? `${name}${jersey}` : "N/A";
        } catch (error) {
          return "N/A";
        }
      },
    },
    {
      field: "coordinates",
      headerName: "Coordinates",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        try {
          return params.row.x && params.row.y
            ? `(${params.row.x}, ${params.row.y})`
            : "N/A";
        } catch (error) {
          return "N/A";
        }
      },
    },
    {
      field: "players_on_ice",
      headerName: "Players on Ice",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        try {
          return `${params.row.home_players_number || 0} vs ${
            params.row.away_players_number || 0
          }`;
        } catch (error) {
          return "N/A";
        }
      },
    },
    {
      field: "score",
      headerName: "Score",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        try {
          return `${params.row.home_score || 0} - ${
            params.row.away_score || 0
          }`;
        } catch (error) {
          return "N/A";
        }
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              params.row.onEdit?.(params.row);
            }}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedEvent(params.row);
              setIsVideoModalOpen(true);
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set("eventId", params.row.id.toString());
              newSearchParams.set("modal", "video");
              navigate(
                { search: newSearchParams.toString() },
                { replace: true }
              );
            }}
            size="small"
            color="primary"
          >
            <PlayCircle />
          </IconButton>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleEditClick = (event: GameEvent) => {
    setSelectedEvent(event);
    setIsEditModalOpen(true);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("eventId", event.id.toString());
    newSearchParams.set("modal", "edit");
    navigate({ search: newSearchParams.toString() }, { replace: true });
  };

  const handleAddClick = () => {
    setSelectedEvent(null);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setIsEditModalOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("eventId");
    newSearchParams.delete("modal");
    navigate({ search: newSearchParams.toString() }, { replace: true });
  };

  const GetGameDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await HttpService.get(
        APIRoutes.GetGameEvents +
          `?game_id=${id}&page=${page}&limit=10&sortOrder=asc&sortBy=id&search=${debouncedSearchQuery}`
      );
      setEvents(res.data?.data?.gameEvents || []);
      setGame(res.data?.data?.game || null);
      setPaginationData(
        res.data?.data?.pagination || {
          total: 0,
          totalPages: 0,
          currentPage: 1,
          limit: 10,
        }
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id, page, debouncedSearchQuery]);

  useEffect(() => {
    GetGameDetail();
  }, [GetGameDetail]);

  const handleRefresh = () => {
    GetGameDetail();
  };
  return (
    <Box sx={{ height: 650, width: "100%", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">Game Events</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box>
            {gameDetails?.away_team && (
              <Chip
                label={gameDetails?.away_team}
                color="primary"
                size="small"
              />
            )}
            AT
            {gameDetails?.home_team && (
              <Chip
                label={gameDetails?.home_team}
                color="primary"
                size="small"
              />
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              navigate(
                PageRoutes.GameViewDetail.replace(":id", id?.toString() || "")
              );
            }}
            startIcon={<Tv />}
          >
            Watch Game
          </Button>
          <TextField
            label="Search Events"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
            placeholder="Search by any field..."
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleAddClick}
            startIcon={<EditIcon />}
          >
            Add Event
          </Button>
        </Box>
      </Box>
      <DataGrid
        rows={events.map((event) => ({
          ...event,
          onEdit: handleEditClick,
        }))}
        columns={columns}
        loading={loading}
        rowCount={paginationData?.total}
        pageSizeOptions={[10]}
        paginationMode="server"
        paginationModel={{
          page: paginationData?.currentPage - 1,
          pageSize: paginationData?.limit,
        }}
        onPaginationModelChange={(model) => {
          setPage(model.page + 1);
        }}
        pagination
        slots={{
          pagination: () => (
            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <Pagination
                count={paginationData?.totalPages || 1}
                page={paginationData?.currentPage || 1}
                onChange={(_, page) => setPage(page)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          ),
        }}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
      />
      <EditFormModal
        teamMap={teamMap}
        sortedPlayers={sortedPlayers}
        open={isEditModalOpen}
        onRefresh={handleRefresh}
        onClose={handleCloseModal}
        data={selectedEvent}
        gameDetails={gameDetails}
      />
      <VideoPlayerDialog
        open={isVideoModalOpen}
        onClose={() => {
          setIsVideoModalOpen(false);
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete("eventId");
          newSearchParams.delete("modal");
          navigate({ search: newSearchParams.toString() }, { replace: true });
        }}
        event_id={selectedEvent?.id?.toString() || ""}
      />
    </Box>
  );
};

export default GameViewDetail;
